import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  sendUserAnswersAction,
  setAnswersAction,
} from 'root-redux/actions/user'
import { selectUUID } from 'root-redux/selects/user'

import { Events, eventLogger } from 'services/eventLogger.service'

import { PageId } from 'page-constants'
import { Language } from 'root-constants'

import { FemiTitle } from '../../styles'
import { FemiButton } from '../FemiButton'
import { StyledFemiFeedbackPopup as S } from './FemiFeedbackPopup.styles'

const FEEDBACK_MAX_LENGTH = 1000
const FEEDBACK_POPUP_TITLE_ID = 'femi-feedback-popup-title'

type TProps = {
  isShown: boolean
  onComplete: () => void
  onClose: () => void
}

export const FemiFeedbackPopup: React.FC<TProps> = ({
  isShown,
  onComplete,
  onClose,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userId = useSelector(selectUUID)

  const [feedback, setFeedback] = useState('')
  const isCompletedRef = useRef(false)

  const trimmedFeedback = feedback.trim()

  const complete = () => {
    if (isCompletedRef.current) return

    isCompletedRef.current = true
    onComplete()
  }

  const handleSend = () => {
    if (isCompletedRef.current) return

    dispatch(
      setAnswersAction({
        pageId: PageId.OTHER_ISSUES,
        answers: trimmedFeedback,
      }),
    )
    dispatch(sendUserAnswersAction())

    eventLogger.logQuestion({
      event: Events.UNSUB_FLOW_QUESTION_4_END,
      question: t('retention.femi.feedbackPopup.description', {
        lng: Language.EN,
      }),
      answers: trimmedFeedback,
      userId,
    })

    complete()
  }

  if (!isShown) return null

  return (
    <S.Overlay>
      <S.Content
        role="dialog"
        aria-modal="true"
        aria-labelledby={FEEDBACK_POPUP_TITLE_ID}
      >
        <S.CloseButton
          type="button"
          aria-label={t('retention.femi.close')}
          onClick={onClose}
        />
        <S.Headline>
          <FemiTitle id={FEEDBACK_POPUP_TITLE_ID}>
            {t('retention.femi.feedbackPopup.title')}
          </FemiTitle>
          <S.Description>
            {t('retention.femi.feedbackPopup.description')}
          </S.Description>
        </S.Headline>
        <S.Textarea
          value={feedback}
          placeholder={t('retention.femi.feedbackPopup.placeholder')}
          maxLength={FEEDBACK_MAX_LENGTH}
          onChange={({ target: { value } }) => setFeedback(value)}
        />
        <S.Buttons>
          <FemiButton
            type="button"
            disabled={!trimmedFeedback}
            onClick={handleSend}
          >
            {t('retention.femi.feedbackPopup.send')}
          </FemiButton>
          <FemiButton type="button" variant="transparent" onClick={complete}>
            {t('retention.femi.skip')}
          </FemiButton>
        </S.Buttons>
      </S.Content>
    </S.Overlay>
  )
}
